import React, { useState } from "react";
import { Alert, Button, Dropdown, Form, Input, Modal, Space, Typography, message } from "antd";
import { DatabaseOutlined, DownOutlined } from "@ant-design/icons";
import { auth } from "../../../../configs/FirebaseConfig";
import { deleteDevOrders } from "../../../../functions/api/devTools";
import { countByStatus, planDevCopy, productCheck, TEST_EMAIL, TEST_PHONE } from "../../../../functions/devTestData";
import { devProjectId, existingDevOrderIds, writeDevOrders } from "../../../../functions/firebase/devOrders";
import { productionProjectId, readProductionOrders } from "../../../../functions/firebase/productionOrders";

const SHOPS = ["VCE", "HVO"];

const projectsOf = (projectId) => SHOPS.map((shop) => projectId(shop) || shop).join(" en ");

/**
 * Bestellingen → Testdata, only in a local or development build (isDevToolsEnvironment): empty the
 * selected delivery date in dev, and copy that date over from production. What
 * scripts/copy-orders did by hand, with the test values fixed: every e-mail address becomes
 * test@digitalkiss.nl, every phone number 0612345678.
 */
const DevTestData = ({ deliveryDate, products }) => {
  const [deleting, setDeleting] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const deleteDate = async () => {
    setDeleting(true);
    const done = [];
    for (const shop of SHOPS) {
      try {
        const result = await deleteDevOrders(shop, deliveryDate);
        done.push(`${shop}: ${result.deleted} verwijderd`);
      } catch (error) {
        message.error(error.message, 10);
      }
    }
    setDeleting(false);
    if (done.length > 0) message.success(`${deliveryDate} in dev: ${done.join(", ")}`);
  };

  const confirmDelete = () =>
    Modal.confirm({
      title: `Alle bestellingen van ${deliveryDate} in dev verwijderen?`,
      content: (
        <>
          <p>
            Elke bestelling met bezorgdatum {deliveryDate} in {projectsOf(devProjectId)}, ook onbetaalde, met haar
            historie. Productie blijft ongemoeid.
          </p>
          <p>Daarna kun je de dag opnieuw uit productie kopiëren.</p>
        </>
      ),
      okText: "Verwijderen",
      okButtonProps: { danger: true },
      cancelText: "Annuleren",
      onOk: deleteDate,
    });

  const items = [
    { key: "delete", danger: true, label: `Bestellingen van ${deliveryDate} verwijderen`, onClick: confirmDelete },
    { key: "copy", label: `Bestellingen van ${deliveryDate} uit productie kopiëren`, onClick: () => setCopyOpen(true) },
  ];

  return (
    <>
      {/* On click, not on hover: one of the two items throws a day of orders away */}
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button danger loading={deleting}>
          <DatabaseOutlined /> Testdata <DownOutlined />
        </Button>
      </Dropdown>
      {copyOpen && <CopyFromProductionModal deliveryDate={deliveryDate} products={products} onClose={() => setCopyOpen(false)} />}
    </>
  );
};

/** Sign in to production, see what would be copied, copy, see what was. */
const CopyFromProductionModal = ({ deliveryDate, products, onClose }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState("login");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const [results, setResults] = useState([]);

  const read = async ({ email, password }) => {
    setBusy(true);
    const found = [];
    for (const shop of SHOPS) {
      try {
        const orders = await readProductionOrders({ shop, email, password, deliveryDate });
        const existing = await existingDevOrderIds(
          shop,
          orders.map((order) => order.id),
        );
        found.push({
          shop,
          plan: planDevCopy(orders, existing),
          products: productCheck(
            orders,
            (products || []).filter((product) => product.shopSource === shop),
          ),
        });
      } catch (error) {
        found.push({ shop, error: error.message });
      }
    }
    // Only needed for the read: not kept for the copy
    form.setFieldsValue({ password: "" });
    setShops(found);
    setBusy(false);
    setStep("preview");
  };

  const copy = async () => {
    setBusy(true);
    const done = [];
    for (const { shop, plan, error } of shops) {
      if (error) continue;
      const creates = plan.filter((entry) => entry.kind === "create");
      try {
        done.push({ shop, ...(creates.length > 0 ? await writeDevOrders(shop, creates) : { written: 0, failed: [] }) });
      } catch (writeError) {
        done.push({ shop, written: 0, failed: [writeError.message] });
      }
    }
    setResults(done);
    setBusy(false);
    setStep("done");
  };

  const toCopy = shops.reduce((total, { plan }) => total + (plan || []).filter((entry) => entry.kind === "create").length, 0);

  const footer = {
    login: [
      <Button key="cancel" onClick={onClose} disabled={busy}>
        Annuleren
      </Button>,
      <Button key="read" type="primary" loading={busy} onClick={() => form.submit()}>
        Lezen uit productie
      </Button>,
    ],
    preview: [
      <Button key="back" onClick={() => setStep("login")} disabled={busy}>
        Terug
      </Button>,
      <Button key="copy" type="primary" loading={busy} disabled={toCopy === 0} onClick={copy}>
        {toCopy} {toCopy === 1 ? "bestelling" : "bestellingen"} naar dev kopiëren
      </Button>,
    ],
    done: [
      <Button key="close" type="primary" onClick={onClose}>
        Sluiten
      </Button>,
    ],
  }[step];

  return (
    <Modal
      open
      title={`Bestellingen van ${deliveryDate} uit productie kopiëren`}
      onCancel={busy ? undefined : onClose}
      closable={!busy}
      maskClosable={false}
      footer={footer}
      width={640}
    >
      {step === "login" && (
        <>
          <Typography.Paragraph>
            Leest de bestellingen met bezorgdatum {deliveryDate} uit productie ({projectsOf(productionProjectId)}) en zet ze in
            dev ({projectsOf(devProjectId)}). Elk e-mailadres wordt <strong>{TEST_EMAIL}</strong>, elk telefoonnummer{" "}
            <strong>{TEST_PHONE}</strong>. Waar een vervoerder de order al heeft (soocool, trunkrsNumber,
            optimoRouteExportedAt) gaat niet mee. Productie wordt alleen gelezen.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            Log in met je CMS-account op productie. Die sessie wordt niet bewaard: hij duurt alleen het lezen.
          </Typography.Paragraph>
          <Form form={form} layout="vertical" onFinish={read} initialValues={{ email: auth.currentUser?.email || "" }}>
            <Form.Item name="email" label="E-mailadres" rules={[{ required: true, message: "Vul je e-mailadres in" }]}>
              <Input autoComplete="username" />
            </Form.Item>
            <Form.Item name="password" label="Wachtwoord" rules={[{ required: true, message: "Vul je wachtwoord in" }]}>
              <Input.Password autoComplete="current-password" />
            </Form.Item>
          </Form>
        </>
      )}

      {step === "preview" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          {shops.map((entry) => (
            <ShopPreview key={entry.shop} entry={entry} deliveryDate={deliveryDate} />
          ))}
        </Space>
      )}

      {step === "done" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          {results.map(({ shop, written, failed }) => (
            <Alert
              key={shop}
              type={failed.length > 0 ? "warning" : "success"}
              showIcon
              message={`${shop}: ${written} ${written === 1 ? "bestelling" : "bestellingen"} in ${devProjectId(shop)} gezet`}
              description={failed.length > 0 ? `Niet gelukt: ${failed.join("; ")}` : undefined}
            />
          ))}
          {shops
            .filter((entry) => entry.error)
            .map(({ shop, error }) => (
              <Alert key={shop} type="error" showIcon message={`${shop}: niet gekopieerd`} description={error} />
            ))}
        </Space>
      )}
    </Modal>
  );
};

/** One shop's part of the copy: how many, which statuses, what stays, what the export will miss. */
const ShopPreview = ({ entry, deliveryDate }) => {
  const { shop, plan, products, error } = entry;
  if (error) return <Alert type="error" showIcon message={`${shop}: niet gelezen`} description={error} />;

  const creates = plan.filter((item) => item.kind === "create");
  const exists = plan.filter((item) => item.kind === "exists");
  const stripped = creates.filter((item) => item.stripped.length > 0).length;
  return (
    <Alert
      type={exists.length > 0 || products.missing.length > 0 ? "warning" : "info"}
      showIcon
      message={`${shop}: ${plan.length} ${plan.length === 1 ? "bestelling" : "bestellingen"} in productie op ${deliveryDate}`}
      description={
        plan.length === 0 ? undefined : (
          <>
            <div>Status: {countByStatus(plan)}</div>
            <div>
              {creates.length} nieuw in {devProjectId(shop)}
              {stripped > 0 && `, waarvan ${stripped} zonder de vervoerdersvelden van productie`}.
            </div>
            {exists.length > 0 && (
              <div>
                {exists.length} {exists.length === 1 ? "staat" : "staan"} al in dev en {exists.length === 1 ? "blijft" : "blijven"}{" "}
                zoals {exists.length === 1 ? "hij is" : "ze zijn"}. Verwijder de dag eerst om ze opnieuw te kopiëren.
              </div>
            )}
            {products.missing.length > 0 && (
              <div>
                {products.missing.length} van de {products.total} producten uit deze bestellingen staan niet in dev (
                {products.missing.slice(0, 5).join(", ")}
                {products.missing.length > 5 ? ", …" : ""}). De SooCool-export rekent die zonder gewicht; draai eerst npm run
                copyproducts.
              </div>
            )}
            {products.withoutPickWeight > 0 && (
              <div>
                {products.withoutPickWeight} van de {products.total} producten hebben in dev geen pickgewicht.
              </div>
            )}
          </>
        )
      }
    />
  );
};

export default DevTestData;
